import React, { useEffect, useState } from "react";
import moment from "moment";
import { startCase, isEmpty } from "lodash";
import { useContextSelector } from "use-context-selector";
import { useTranslation } from "react-i18next";
import Layout from "../Layout";
import AuthContext from "../../context/AuthContext";
import { apiGet } from "../../Utils/apiFetch";
import apiPath from "../../Utils/apiPath";
import { formatWithCommas } from "../../Utils/Toast";

const TransactionRecord = () => {
  const { t } = useTranslation();
  const previousDate = moment().subtract(6, "days").format("YYYY-MM-DD");
  const currentDate = moment().format("YYYY-MM-DD");

  const user = useContextSelector(AuthContext, (ctx) => ctx.user);

  const [activeTab, setActiveTab] = useState("deposit"); // "deposit" | "withdrawal"
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState({
    fromPeriod: previousDate,
    toPeriod: currentDate,
  });

  const getData = async (tab, range) => {
    try {
      const apiCall =
        tab === "withdrawal" ? apiPath.withDrawHistory : apiPath.depositHistory;

      const res = await apiGet(apiCall, {
        fromPeriod: range.fromPeriod,
        toPeriod: range.toPeriod,
      });

      const results =
        res?.status === 200 && res?.data?.success ? res.data.results : [];

      setData(results);
    } catch (error) {
      setData([]);
    }
  };

  useEffect(() => {
    if (!isEmpty(user)) {
      getData(activeTab, filter);
    }
  }, [user, activeTab, filter]);

  const handleFromChange = (e) => {
    const value = e.target.value;
    setFilter((prev) => ({
      ...prev,
      fromPeriod: value,
      // keep the range valid if "from" is pushed past "to"
      toPeriod: prev.toPeriod && value > prev.toPeriod ? value : prev.toPeriod,
    }));
  };

  const handleToChange = (e) => {
    const value = e.target.value;
    setFilter((prev) => ({
      ...prev,
      toPeriod: value,
    }));
  };


  const filteredData = data.filter((item) => {
    if (!search) return true;
    const query = search.toLowerCase();
    return (
      item?.TransactionId?.toLowerCase()?.includes(query) ||
      item?._id?.toLowerCase()?.includes(query) ||
      startCase(item?.type)?.toLowerCase()?.includes(query)
    );
  });

  return (
    <Layout>
      <div>
        <div style={{ background: "white" }} className="table-wrapper">
          <div className="elevation-0 my-2 search-container v-card v-sheet theme--light">
            <div className="col col-12">
              <div cols={12} className="row no-gutters">
                <button
                  type="button"
                  onClick={() => setActiveTab("deposit")}
                  className={`history-tab v-btn v-btn--has-bg theme--light v-size--default ${
                    activeTab === "deposit" ? "primary" : "outlined"
                  }`}
                >
                  <span className="v-btn__content">
                    <span>{t("Deposit")}</span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("withdrawal")}
                  className={`history-tab v-btn v-btn--has-bg theme--light v-size--default ${
                    activeTab === "withdrawal" ? "primary" : "outlined"
                  }`}
                >
                  <span className="v-btn__content">
                    <span>{t("Withdrawal")}</span>
                  </span>
                </button>
              </div>
            </div>

            <div className="row pa-2 no-gutters">
              <div className="col-sm-4 col-md-3 col-6">
                <div className="v-input date-search elevation-0 v-input--hide-details v-input--is-label-active v-input--is-dirty v-input--dense theme--light v-text-field v-text-field--is-booted v-text-field--enclosed v-text-field--outlined">
                  <div className="v-input__control">
                    <div className="v-input__slot">
                      <fieldset aria-hidden="true">
                        <legend style={{ width: "0px" }}>
                          <span className="notranslate">​</span>
                        </legend>
                      </fieldset>
                      <div className="v-text-field__slot">
                        <input
                          id="input-from-date"
                          type="date"
                          max={filter.toPeriod || undefined}
                          value={filter.fromPeriod}
                          onChange={handleFromChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-sm-4 col-md-3 col-6">
                <div className="v-input date-search elevation-0 v-input--hide-details v-input--is-label-active v-input--is-dirty v-input--dense theme--light v-text-field v-text-field--is-booted v-text-field--enclosed v-text-field--outlined">
                  <div className="v-input__control">
                    <div className="v-input__slot">
                      <fieldset aria-hidden="true">
                        <legend style={{ width: "0px" }}>
                          <span className="notranslate">​</span>
                        </legend>
                      </fieldset>
                      <div className="v-text-field__slot">
                        <input
                          id="input-to-date"
                          type="date"
                          min={filter.fromPeriod || undefined}
                          max={currentDate}
                          value={filter.toPeriod}
                          onChange={handleToChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="row no-gutters">
              <div className="col col-12" />
            </div>
            <div className="row">
              <div className="col col-12">
                {filteredData?.length > 0 ? (
                  filteredData.map((item) => (
                    <div
                      key={item?._id || item?.TransactionId}
                      className="row mx-1 mobile-reward-table"
                    >
                      <div className="mr-0 col col-6">
                        <div className="row reward-table-header pl-6 no-gutters">
                          <div className="col col-12">{t("TransactionDate")}</div>
                          <div className="col col-12">{t("TransactionIdPlaceholder")}</div>

                          <div className="col col-12">{t("Amount")}</div>
                          <div className="col col-12">{t("Type")}</div>
                          <div className="col col-12">{t("Status")}</div>
                        </div>
                      </div>
                      <div className="col col-6">
                        <div className="row no-gutters">
                          <div className="col col-12">
                            {moment(item?.createdAt).format(
                              "YYYY-MM-DD HH:mm:ss",
                            )}
                          </div>
                          <div className="col col-12">
                            {item?.TransactionId || item?._id}
                          </div>

                          <div className="col col-12">
                            {formatWithCommas(item?.amount)}
                          </div>
                          <div className="col col-12">
                            {startCase(item?.type)}
                          </div>
                          <div
                            style={{
                              color:
                                item?.status == "completed" ? "green" : "red",
                            }}
                            className="col col-12"
                          >
                            {item?.status == "Expired Payment Link"
                              ? t("Expired")
                              : item?.status == "completed"
                                ? t("Approved")
                                : item?.status == "requested"
                                  ? t("Processing")
                                  : startCase(item?.status)}
                          </div>
                        </div>
                      </div>
                      <div className="col col-12">
                        <hr
                          role="separator"
                          aria-orientation="horizontal"
                          className="mx-2 v-divider theme--light"
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="row">
                    <div className="text-center pt-0 pb-8 col col-12">
                      <label className="history-table-header subtitle-2">
                        {t("NoDataAvailable")}
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

 
      </div>
    </Layout>
  );
};

export default TransactionRecord;
